import { useState, useCallback, useEffect } from 'react';
import { FactoryGalleryImage, Certification } from '../types/storefront.types';
import { authService, sellerService } from '@b2b/services';

export interface SocialLinks {
  catalogue1?: string;
  catalogue2?: string;
  catalogue3?: string;
  meetLink?: string;
  youtube?: string;
}

interface BusinessInfo {
  businessLicense?: string;
  taxId?: string;
  gstNumber?: string;
  panNumber?: string;
  registrationNumber?: string;
  productionCapacity?: string;
  employeeCount?: number;
  factorySize?: string;
  qualityCertifications?: string[];
  yearsInBusiness?: number;
}

interface UseStorefrontSettingsProps {
  initialCompanyDescription?: string;
  initialGallery?: FactoryGalleryImage[];
  initialCertifications?: Certification[];
  initialBusinessInfo?: BusinessInfo;
  onImageUpload?: (file: File) => Promise<string>;
}

export const useStorefrontSettings = ({
  initialCompanyDescription = '',
  initialGallery = [],
  initialCertifications = [],
  initialBusinessInfo = {},
  onImageUpload,
}: UseStorefrontSettingsProps = {}) => {
  const [companyDescription, setCompanyDescription] = useState(initialCompanyDescription);
  const [gallery, setGallery] = useState<FactoryGalleryImage[]>(initialGallery);
  const [certifications, setCertifications] = useState<Certification[]>(initialCertifications);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>(initialBusinessInfo);
  const [isLoading, setIsLoading] = useState(false);
  /** Increments only when the initial GET storefront settings request finishes (for baseline sync; avoids missing isLoading transitions when React batches updates). */
  const [storefrontSettingsLoadVersion, setStorefrontSettingsLoadVersion] = useState(0);

  // Shop Settings Basic Info (from company + storefront)
  const [shopName, setShopName] = useState('');
  const [shopPhone, setShopPhone] = useState('');
  const [shopAddress, setShopAddress] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);

  // Social Media / Catalogue links (optional)
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({});

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      if (onImageUpload) {
        return onImageUpload(file);
      }
      return URL.createObjectURL(file);
    },
    [onImageUpload]
  );

  const updateCompanyDescription = useCallback((description: string) => {
    setCompanyDescription(description);
  }, []);

  const updateGallery = useCallback((newGallery: FactoryGalleryImage[]) => {
    setGallery(newGallery);
  }, []);

  const updateCertifications = useCallback((newCertifications: Certification[]) => {
    setCertifications(newCertifications);
  }, []);

  const updateBusinessInfo = useCallback((newInfo: Partial<BusinessInfo>) => {
    setBusinessInfo((prev) => ({ ...prev, ...newInfo }));
  }, []);

  // Wrapper for setBusinessInfo that merges instead of replacing
  const setBusinessInfoSafe = useCallback((newInfo: BusinessInfo) => {
    setBusinessInfo((prev) => ({ ...prev, ...newInfo }));
  }, []);

  // Load initial data from API
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const settingsResponse = await sellerService.getStorefrontSettings();
        if (settingsResponse.success && settingsResponse.data) {
          const settingsData = settingsResponse.data;
          
          // Shop Settings Basic Info: company (from GET) + storefront fields
          if (settingsData.company) {
            setCompanyId(settingsData.company.id || settingsData.companyId || undefined);
            setShopName(settingsData.company.name || '');
            setShopPhone(settingsData.company.phone || '');
            setShopAddress(settingsData.company.address || '');
          } else if (settingsData.companyId) {
            setCompanyId(settingsData.companyId);
          }
          if (settingsData.logoUrl != null) setLogoUrl(settingsData.logoUrl || null);
          if (settingsData.seoTitle != null) setSeoTitle(settingsData.seoTitle || '');
          if (settingsData.seoDescription != null) setSeoDescription(settingsData.seoDescription || '');
          
          // Social links (optional)
          if (settingsData.socialLinks && typeof settingsData.socialLinks === 'object') {
            setSocialLinks({
              catalogue1: settingsData.socialLinks.catalogue1 || '',
              catalogue2: settingsData.socialLinks.catalogue2 || '',
              catalogue3: settingsData.socialLinks.catalogue3 || '',
              meetLink: settingsData.socialLinks.meetLink || '',
              youtube: settingsData.socialLinks.youtube || '',
            });
          }
          
          // Load company description
          if (settingsData.companyDescription) {
            setCompanyDescription(settingsData.companyDescription);
          }
          
          // Load gallery images
          if (settingsData.gallery && Array.isArray(settingsData.gallery)) {
            setGallery(settingsData.gallery.map((img: any, index: number) => ({
              id: img.id || `gallery-${index}`,
              imageUrl: img.imageUrl,
              caption: img.caption || '',
              order: img.order || index,
            })));
          }
          
          // Load certifications
          if (settingsData.certifications && Array.isArray(settingsData.certifications)) {
            setCertifications(settingsData.certifications.map((cert: any, index: number) => ({
              id: cert.id || `cert-${index}`,
              name: cert.name || '',
              imageUrl: cert.imageUrl || '',
              issuingAuthority: cert.issuingAuthority,
              issueDate: cert.issueDate,
              expiryDate: cert.expiryDate,
              verificationStatus: (cert.verificationStatus || 'pending') as 'pending' | 'verified' | 'expired',
              order: cert.order || index,
            })));
          }
          
          // Load business info - include ALL fields
          // Always set businessInfo with all fields, using empty defaults for missing ones
          setBusinessInfo({
            businessLicense: settingsData.businessInfo?.businessLicense || '',
            taxId: settingsData.businessInfo?.taxId || '',
            registrationNumber: settingsData.businessInfo?.registrationNumber || '',
            productionCapacity: settingsData.businessInfo?.productionCapacity || '',
            factorySize: settingsData.businessInfo?.factorySize || '',
            qualityCertifications: settingsData.businessInfo?.qualityCertifications || [],
            gstNumber: settingsData.businessInfo?.gstNumber || '',
            panNumber: settingsData.businessInfo?.panNumber || '',
            employeeCount: settingsData.businessInfo?.employeeCount,
            yearsInBusiness: settingsData.businessInfo?.yearsInBusiness,
          });
        }
      } catch (error) {
        console.error('Failed to load storefront settings:', error);
      } finally {
        setIsLoading(false);
        setStorefrontSettingsLoadVersion((v) => v + 1);
      }
    };

    loadData();
  }, []);

  const saveSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Save company description, gallery, certifications, and business info
      // Always send current state (including empty arrays) to properly clear data
      const settingsPayload: any = {};
      
      // Always include companyDescription (even if empty string)
      settingsPayload.companyDescription = companyDescription || '';
      
      // Always include gallery (even if empty array) to allow clearing
      settingsPayload.gallery = gallery.map(img => ({
        imageUrl: img.imageUrl,
        caption: img.caption || '',
        order: img.order,
      }));
      
      // Always include certifications (even if empty array) to allow clearing
      settingsPayload.certifications = certifications.map(cert => ({
        name: cert.name || '',
        imageUrl: cert.imageUrl || '',
        issuingAuthority: cert.issuingAuthority,
        issueDate: cert.issueDate,
        expiryDate: cert.expiryDate,
        verificationStatus: cert.verificationStatus || 'pending',
        order: cert.order,
      }));
      
      // Always include businessInfo (even if empty object) to allow clearing
      // Send null for empty strings to properly clear fields in backend
      // Include ALL fields from BusinessInfo interface
      settingsPayload.businessInfo = {
        businessLicense: businessInfo.businessLicense && businessInfo.businessLicense.trim() !== '' ? businessInfo.businessLicense : null,
        taxId: businessInfo.taxId && businessInfo.taxId.trim() !== '' ? businessInfo.taxId : null,
        registrationNumber: businessInfo.registrationNumber && businessInfo.registrationNumber.trim() !== '' ? businessInfo.registrationNumber : null,
        productionCapacity: businessInfo.productionCapacity && businessInfo.productionCapacity.trim() !== '' ? businessInfo.productionCapacity : null,
        factorySize: businessInfo.factorySize && businessInfo.factorySize.trim() !== '' ? businessInfo.factorySize : null,
        qualityCertifications: businessInfo.qualityCertifications && Array.isArray(businessInfo.qualityCertifications) ? businessInfo.qualityCertifications : null,
        gstNumber: businessInfo.gstNumber && businessInfo.gstNumber.trim() !== '' ? businessInfo.gstNumber : null,
        panNumber: businessInfo.panNumber && businessInfo.panNumber.trim() !== '' ? businessInfo.panNumber : null,
        employeeCount: businessInfo.employeeCount !== undefined && businessInfo.employeeCount !== null ? businessInfo.employeeCount : null,
        yearsInBusiness: businessInfo.yearsInBusiness !== undefined && businessInfo.yearsInBusiness !== null ? businessInfo.yearsInBusiness : null,
      };

      // Always call API to save current state
      await sellerService.updateStorefrontSettings(settingsPayload);
    } catch (error) {
      console.error('Failed to save settings:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [companyDescription, gallery, certifications, businessInfo]);

  /** Save Shop Settings Basic Info only (shop name, phone, address, logo, meta title/description) */
  const saveBasicInfo = useCallback(async (payload: {
    shopName: string;
    shopPhone: string;
    shopAddress: string;
    logoUrl?: string | null;
    seoTitle: string;
    seoDescription: string;
  }) => {
    setIsLoading(true);
    try {
      const nextShopName = (payload.shopName || "").trim();
      if (nextShopName) {
        const { available } = await authService.checkShopNameAvailable(
          nextShopName,
          companyId,
        );
        if (!available) {
          throw new Error(
            "This shop name is already taken. Please choose a different one.",
          );
        }
      }
      await sellerService.updateStorefrontSettings({
        shopName: payload.shopName || undefined,
        shopPhone: payload.shopPhone || undefined,
        shopAddress: payload.shopAddress || undefined,
        logoUrl: payload.logoUrl ?? undefined,
        seoTitle: payload.seoTitle || undefined,
        seoDescription: payload.seoDescription || undefined,
      });
    } catch (error) {
      console.error('Failed to save basic info:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  /** Save Social Media / Catalogue links only */
  const saveSocialLinks = useCallback(async (links: SocialLinks) => {
    setIsLoading(true);
    try {
      await sellerService.updateStorefrontSettings({
        businessHours: { socialLinks: links },
      });
    } catch (error) {
      console.error('Failed to save social links:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const restoreSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Force refresh to get latest saved data from API
      const settingsResponse = await sellerService.getStorefrontSettings(true);
      if (settingsResponse.success && settingsResponse.data) {
        const settingsData = settingsResponse.data;
        
        // Restore company description
        setCompanyDescription(settingsData.companyDescription || '');
        
        // Restore Shop Settings Basic Info
        if (settingsData.company) {
          setShopName(settingsData.company.name || '');
          setShopPhone(settingsData.company.phone || '');
          setShopAddress(settingsData.company.address || '');
        }
        if (settingsData.logoUrl != null) setLogoUrl(settingsData.logoUrl || null);
        if (settingsData.seoTitle != null) setSeoTitle(settingsData.seoTitle || '');
        if (settingsData.seoDescription != null) setSeoDescription(settingsData.seoDescription || '');
        if (settingsData.socialLinks && typeof settingsData.socialLinks === 'object') {
          setSocialLinks({
            catalogue1: settingsData.socialLinks.catalogue1 || '',
            catalogue2: settingsData.socialLinks.catalogue2 || '',
            catalogue3: settingsData.socialLinks.catalogue3 || '',
            meetLink: settingsData.socialLinks.meetLink || '',
            youtube: settingsData.socialLinks.youtube || '',
          });
        } else {
          setSocialLinks({});
        }
        
        // Restore gallery images
        if (settingsData.gallery && Array.isArray(settingsData.gallery)) {
          setGallery(settingsData.gallery.map((img: any, index: number) => ({
            id: img.id || `gallery-${index}`,
            imageUrl: img.imageUrl,
            caption: img.caption || '',
            order: img.order || index,
          })));
        } else {
          setGallery([]);
        }
        
        // Restore certifications
        if (settingsData.certifications && Array.isArray(settingsData.certifications)) {
          setCertifications(settingsData.certifications.map((cert: any, index: number) => ({
            id: cert.id || `cert-${index}`,
            name: cert.name || '',
            imageUrl: cert.imageUrl || '',
            issuingAuthority: cert.issuingAuthority,
            issueDate: cert.issueDate,
            expiryDate: cert.expiryDate,
            verificationStatus: (cert.verificationStatus || 'pending') as 'pending' | 'verified' | 'expired',
            order: cert.order || index,
          })));
        } else {
          setCertifications([]);
        }
        
        // Restore business info - include ALL fields
        // Always set businessInfo with all fields, using empty defaults for missing ones
        setBusinessInfo({
          businessLicense: settingsData.businessInfo?.businessLicense || '',
          taxId: settingsData.businessInfo?.taxId || '',
          registrationNumber: settingsData.businessInfo?.registrationNumber || '',
          productionCapacity: settingsData.businessInfo?.productionCapacity || '',
          factorySize: settingsData.businessInfo?.factorySize || '',
          qualityCertifications: settingsData.businessInfo?.qualityCertifications || [],
          gstNumber: settingsData.businessInfo?.gstNumber || '',
          panNumber: settingsData.businessInfo?.panNumber || '',
          employeeCount: settingsData.businessInfo?.employeeCount,
          yearsInBusiness: settingsData.businessInfo?.yearsInBusiness,
        });
        if (settingsData.socialLinks && typeof settingsData.socialLinks === 'object') {
          setSocialLinks({
            catalogue1: settingsData.socialLinks.catalogue1 || '',
            catalogue2: settingsData.socialLinks.catalogue2 || '',
            catalogue3: settingsData.socialLinks.catalogue3 || '',
            meetLink: settingsData.socialLinks.meetLink || '',
            youtube: settingsData.socialLinks.youtube || '',
          });
        } else {
          setSocialLinks({});
        }
      }
    } catch (error) {
      console.error('Failed to restore settings:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    companyDescription,
    gallery,
    certifications,
    businessInfo,
    isLoading,
    storefrontSettingsLoadVersion,
    shopName,
    shopPhone,
    shopAddress,
    logoUrl,
    seoTitle,
    seoDescription,
    socialLinks,
    setShopName,
    setShopPhone,
    setShopAddress,
    setLogoUrl,
    setSeoTitle,
    setSeoDescription,
    setSocialLinks,
    setCompanyDescription,
    setGallery,
    setCertifications,
    setBusinessInfo: setBusinessInfoSafe, // Use safe setter that merges
    updateCompanyDescription,
    updateGallery,
    updateCertifications,
    updateBusinessInfo,
    handleImageUpload,
    saveSettings,
    saveBasicInfo,
    saveSocialLinks,
    restoreSettings,
  };
};

